<template>
	<div class="bill-calculation-page">
		<!-- 顶部操作区 -->
		<el-card class="action-card" shadow="hover">
			<div class="header-content">
				<h3 class="page-title">客户账单计算</h3>
				<div class="action-buttons">
					<el-upload 
						:disabled="isCalculating" 
						:limit="1" 
						action="https://open.jfcorp.cn/express/uploadFile"
						:data="{ 'fileClass': 'calculateBill' }" 
						:on-success="handleUploadSuccess" 
						:show-file-list="false"
						accept=".xlsx"
						class="upload-btn"
					>
						<el-button type="primary" :loading="isUploading">
							<el-icon style="margin-right: 5px;"><Upload /></el-icon>
							上传原始数据
						</el-button>
					</el-upload>
					
					<el-button 
						type="success" 
						:disabled="!billList.length" 
						@click="saveBillWindow = true"
					>
						<el-icon style="margin-right: 5px;"><Save /></el-icon>
						保存为客户账单
					</el-button>

					<el-button 
						link 
						type="danger" 
						v-if="errorList.length > 0" 
						@click="showErrorDrawer = true"
					>
						<el-icon style="margin-right: 5px;"><Warning /></el-icon>
						查看错误列表 ({{ errorList.length }})
					</el-button>
				</div>
			</div>
		</el-card>

		<!-- 数据表格区 -->
		<el-card class="table-card" shadow="never">
			<el-table 
				:data="billList" 
				height="calc(100vh - 280px)" 
				show-summary
				:summary-method="getSummaries"
				:row-class-name="tableRowClassName" 
				style="width: 100%" 
				border
				stripe
				@selection-change="handleSelectionChange"
			>
				<el-table-column type="selection" width="50" align="center" />
				<el-table-column label="序号" type="index" width="60" align="center" />
				
				<el-table-column prop="waybillNo" label="运单号" min-width="120" fixed="left">
					<template #default="scope">
						<span class="waybill-link" @click="copyWaybill(scope.row.waybillNo)" title="点击复制">
							{{ scope.row.waybillNo }}
						</span>
					</template>
				</el-table-column>
				
				<el-table-column prop="customerName" label="客户名称" min-width="120" show-overflow-tooltip />
				<el-table-column prop="shopName" label="店铺名称" min-width="120" show-overflow-tooltip />
				
				<el-table-column prop="chargedWeight" label="计费重量" width="100" align="right">
					<template #default="scope">{{ Number(scope.row.chargedWeight).toFixed(2) }}</template>
				</el-table-column>
				<el-table-column prop="hdWeight" label="核对重量" width="100" align="right" />
				<el-table-column prop="lsWeight" label="揽收重量" width="100" align="right" />
				
				<el-table-column prop="destination" label="目的地" width="100" align="center" />
				<el-table-column prop="destinationOrg" label="目的网点" width="120" show-overflow-tooltip />
				<el-table-column prop="serviceProducts" label="服务产品" width="100" align="center" />
				
				<el-table-column prop="totalAmount" label="运费" width="100" align="right">
					<template #default="scope">{{ Number(scope.row.totalAmount).toFixed(2) }}</template>
				</el-table-column>
				<el-table-column prop="cost" label="出港成本" width="100" align="right">
					<template #default="scope">{{ Number(scope.row.cost).toFixed(2) }}</template>
				</el-table-column>
				<el-table-column prop="profit" label="利润" width="100" align="right">
					<template #default="scope">
						<span :class="scope.row.profit >= 0 ? 'text-profit' : 'text-loss'">
							{{ Number(scope.row.profit).toFixed(2) }}
						</span>
					</template>
				</el-table-column>
				
				<el-table-column prop="date" label="账单日期" width="120" align="center" />
				<el-table-column prop="remark" label="备注" min-width="150" show-overflow-tooltip />
				
				<el-table-column label="操作" width="120" align="center" fixed="right">
					<template #default="scope">
						<el-button link type="primary" size="small" @click="openEditDialog(scope.row, scope.$index)">
							修改
						</el-button>
						<el-popconfirm title="确定删除此项吗？" @confirm="deleteBillItem(scope.$index)">
							<template #reference>
								<el-button link type="danger" size="small">删除</el-button>
							</template>
						</el-popconfirm>
					</template>
				</el-table-column>
			</el-table>
			
			<el-empty v-if="!isCalculating && billList.length === 0" description="请上传Excel文件开始计算" />
		</el-card>

		<!-- 底部固定操作栏 (当有数据时显示) -->
		<div v-if="billList.length > 0" class="footer-bar">
			<div class="selection-info">
				已选择 <span class="highlight">{{ selectedRows.length }}</span> 条记录
			</div>
			<div class="footer-actions">
				<el-button @click="billList = []">清空数据</el-button>
				<el-button type="primary" @click="saveBillWindow = true" :disabled="selectedRows.length === 0">
					保存选中账单
				</el-button>
			</div>
		</div>

		<!-- 计费配置弹窗 -->
		<el-dialog 
			v-model="configDialogVisible" 
			title="计费配置" 
			width="600px" 
			:close-on-click-modal="false"
			@close="resetConfigForm"
		>
			<el-form :model="configForm" label-width="100px" class="config-form">
				<el-divider content-position="left">基础设置</el-divider>
				<el-form-item label="计费价格表" prop="priceListId">
					<el-select v-model="configForm.priceListId" filterable placeholder="请选择" style="width: 100%;">
						<el-option v-for="item in priceList" :key="item.priceListId" :label="item.priceListName" :value="item.priceListId" />
					</el-select>
				</el-form-item>

				<el-divider content-position="left">列名映射 (自动匹配)</el-divider>
				<el-row :gutter="20">
					<el-col :span="12">
						<el-form-item label="运单号">
							<el-select v-model="configForm.column.waybillNo" filterable clearable placeholder="选择列">
								<el-option v-for="item in tableHeader" :key="item.index" :label="item.value" :value="item.index" />
							</el-select>
						</el-form-item>
					</el-col>
					<el-col :span="12">
						<el-form-item label="计费重量">
							<el-select v-model="configForm.column.chargedWeight" filterable clearable placeholder="选择列">
								<el-option v-for="item in tableHeader" :key="item.index" :label="item.value" :value="item.index" />
							</el-select>
						</el-form-item>
					</el-col>
					<el-col :span="12">
						<el-form-item label="客户名称">
							<el-select v-model="configForm.column.customerName" filterable clearable placeholder="选择列">
								<el-option v-for="item in tableHeader" :key="item.index" :label="item.value" :value="item.index" />
							</el-select>
						</el-form-item>
					</el-col>
					<el-col :span="12">
						<el-form-item label="店铺名称">
							<el-select v-model="configForm.column.shopName" filterable clearable placeholder="选择列">
								<el-option v-for="item in tableHeader" :key="item.index" :label="item.value" :value="item.index" />
							</el-select>
						</el-form-item>
					</el-col>
					<el-col :span="12">
						<el-form-item label="目的地">
							<el-select v-model="configForm.column.destination" filterable clearable placeholder="选择列">
								<el-option v-for="item in tableHeader" :key="item.index" :label="item.value" :value="item.index" />
							</el-select>
						</el-form-item>
					</el-col>
					<el-col :span="12">
						<el-form-item label="目的网点">
							<el-select v-model="configForm.column.destinationOrg" filterable clearable placeholder="选择列">
								<el-option v-for="item in tableHeader" :key="item.index" :label="item.value" :value="item.index" />
							</el-select>
						</el-form-item>
					</el-col>
					<el-col :span="12">
						<el-form-item label="服务产品">
							<el-select v-model="configForm.column.serviceProducts" filterable clearable placeholder="选择列">
								<el-option v-for="item in tableHeader" :key="item.index" :label="item.value" :value="item.index" />
							</el-select>
						</el-form-item>
					</el-col>
					<el-col :span="12">
						<el-form-item label="成本金额">
							<el-select v-model="configForm.column.cost" filterable clearable placeholder="选择列">
								<el-option v-for="item in tableHeader" :key="item.index" :label="item.value" :value="item.index" />
							</el-select>
						</el-form-item>
					</el-col>
					<el-col :span="12">
						<el-form-item label="账单日期">
							<el-select v-model="configForm.column.date" filterable clearable placeholder="选择列">
								<el-option v-for="item in tableHeader" :key="item.index" :label="item.value" :value="item.index" />
							</el-select>
						</el-form-item>
					</el-col>
					<el-col :span="12">
						<el-form-item label="备注">
							<el-select v-model="configForm.column.remark" filterable clearable placeholder="选择列">
								<el-option v-for="item in tableHeader" :key="item.index" :label="item.value" :value="item.index" />
							</el-select>
						</el-form-item>
					</el-col>
				</el-row>

				<el-divider content-position="left">数据筛选 (可选)</el-divider>
				<el-form-item label="筛选客户">
					<el-select v-model="configForm.screen[0].keyword" multiple filterable allow-create placeholder="输入客户名回车添加">
					</el-select>
				</el-form-item>
				<el-form-item label="筛选店铺">
					<el-select v-model="configForm.screen[1].keyword" multiple filterable allow-create placeholder="输入店铺名回车添加">
					</el-select>
				</el-form-item>
				
				<div class="dialog-tip">
					<el-icon><InfoFilled /></el-icon>
					<span>红色标记为必填项，其他可根据实际表格情况调整</span>
				</div>
			</el-form>
			<template #footer>
				<el-button @click="configDialogVisible = false">取消</el-button>
				<el-button type="primary" @click="submitCalculation" :loading="isCalculating">开始计算</el-button>
			</template>
		</el-dialog>

		<!-- 修改账单项弹窗 -->
		<el-dialog v-model="editDialogVisible" title="修改账单明细" width="500px">
			<el-form :model="editForm" label-width="90px">
				<el-form-item label="运单号"><el-input v-model="editForm.waybillNo" disabled /></el-form-item>
				<el-form-item label="客户名称"><el-input v-model="editForm.customerName" /></el-form-item>
				<el-form-item label="店铺名称"><el-input v-model="editForm.shopName" /></el-form-item>
				<el-form-item label="目的地"><el-input v-model="editForm.destination" /></el-form-item>
				<el-form-item label="计费重量"><el-input v-model="editForm.chargedWeight" type="number" /></el-form-item>
				<el-form-item label="运费"><el-input v-model="editForm.totalAmount" type="number" /></el-form-item>
				<el-form-item label="出港成本"><el-input v-model="editForm.cost" type="number" /></el-form-item>
				<el-form-item label="账单日期">
					<el-date-picker v-model="editForm.date" type="datetime" placeholder="选择日期" style="width: 100%;" />
				</el-form-item>
				<el-form-item label="备注"><el-input v-model="editForm.remark" type="textarea" rows="2" /></el-form-item>
			</el-form>
			<template #footer>
				<el-button @click="editDialogVisible = false">取消</el-button>
				<el-button type="primary" @click="saveEdit">保存</el-button>
			</template>
		</el-dialog>

		<!-- 保存账单弹窗 -->
		<el-dialog v-model="saveBillWindow" title="保存账单" width="400px">
			<el-form :model="saveForm" label-width="80px">
				<el-form-item label="账单名称">
					<el-input v-model="saveForm.billName" placeholder="例如：2023年10月顺丰账单" />
				</el-form-item>
				<el-form-item label="结算客户">
					<el-select v-model="saveForm.customerId" filterable placeholder="请选择" style="width: 100%;">
						<el-option v-for="item in customerList" :key="item.customerId" :label="item.customerName" :value="item.customerId" />
					</el-select>
				</el-form-item>
			</el-form>
			<template #footer>
				<el-button @click="saveBillWindow = false">取消</el-button>
				<el-button type="primary" @click="confirmSaveBill" :loading="isSaving">提交保存</el-button>
			</template>
		</el-dialog>

		<!-- 错误列表抽屉 -->
		<el-drawer v-model="showErrorDrawer" title="计费错误详情" direction="rtl" size="30%">
			<div class="error-list-container">
				<el-alert 
					v-for="(err, index) in errorList" 
					:key="index" 
					:title="err" 
					type="error" 
					show-icon 
					closable 
					class="error-item"
				/>
			</div>
		</el-drawer>
	</div>
</template>

<script>
	import { Upload, Save, Warning, InfoFilled } from '@element-plus/icons-vue';

	export default {
		components: { Upload, Save, Warning, InfoFilled },
		data() {
			return {
				isUploading: false,
				isCalculating: false,
				isSaving: false,
				
				tableHeader: [],
				priceList: [],
				customerList: [], // 假设这里会有客户列表
				
				billList: [],
				selectedRows: [],
				errorList: [],
				
				configDialogVisible: false,
				editDialogVisible: false,
				saveBillWindow: false,
				showErrorDrawer: false,
				
				configForm: {
					priceListId: null,
					tablePath: null,
					column: {
						waybillNo: null,
						customerName: null,
						shopName: null,
						destination: null,
						destinationOrg: null,
						chargedWeight: null,
						serviceProducts: null,
						cost: null,
						remark: null,
						date: null
					},
					screen: [
						{ parameterName: 'customerName', filteringMethod: 'CONTAIN', keyword: [] },
						{ parameterName: 'shopName', filteringMethod: 'CONTAIN', keyword: [] }
					]
				},
				
				editForm: {},
				editIndex: -1,
				
				saveForm: {
					billName: '',
					customerId: null,
					billList: []
				}
			}
		},
		methods: {
			// 上传成功
			handleUploadSuccess(response) {
				if (response.status) {
					this.isUploading = false;
					this.configForm.tablePath = response.data.file;
					
					// 获取表头并自动匹配
					this.apiService.post({
						modules: "tableHeader",
						content: { filePath: response.data.file }
					}).then(res => {
						if (res.status) {
							this.tableHeader = res.data.headerData;
							this.autoMapColumns();
							this.configDialogVisible = true;
						}
					});
				} else {
					this.isUploading = false;
					this.$message.error(response.msg || '上传失败');
				}
			},
			
			// 智能列名匹配
			autoMapColumns() {
				const mapField = (keywords, field) => {
					for (let item of this.tableHeader) {
						const name = String(item.value);
						if (keywords.some(k => name.includes(k))) {
							this.configForm.column[field] = item.index;
							break;
						}
					}
				};

				mapField(['运单号'], 'waybillNo');
				mapField(['物料客户', '客户', '对象'], 'customerName');
				mapField(['商家', '店铺'], 'shopName');
				mapField(['下车重量一', '重量'], 'chargedWeight');
				mapField(['省份', '目的地'], 'destination');
				mapField(['签收网点', '网点', '组织'], 'destinationOrg');
				mapField(['服务', '时效'], 'serviceProducts');
				mapField(['成本', '金额'], 'cost');
				mapField(['揽收时间', '揽收日期', '日期', '时间'], 'date');
			},
			
			resetConfigForm() {
				// 可以选择是否重置，这里保留映射方便下次使用
			},

			// 提交计算
			submitCalculation() {
				if (!this.configForm.priceListId) {
					this.$message.warning('请选择计费价格表');
					return;
				}
				if (!this.configForm.column.waybillNo || !this.configForm.column.chargedWeight) {
					this.$message.warning('请至少映射运单号和计费重量列');
					return;
				}

				this.isCalculating = true;
				this.configDialogVisible = false;
				this.errorList = [];
				this.billList = [];

				this.apiService.post({
					modules: "calculateFreight",
					content: this.configForm
				}).then(res => {
					if (res.status) {
						const list = res.data.billList || [];
						this.billList = list.map(item => ({
							...item,
							profit: (Number(item.totalAmount) - Number(item.cost)).toFixed(2)
						}));
						
						if (res.data.errorList && res.data.errorList.length > 0) {
							this.errorList = res.data.errorList;
							this.showErrorDrawer = true;
						}
						this.$message.success(`计算完成，共 ${list.length} 条数据`);
					} else {
						this.$message.error(res.msg || '计算失败');
					}
				}).catch(err => {
					console.error(err);
					this.$message.error('网络异常');
				}).finally(() => {
					this.isCalculating = false;
				});
			},

			// 表格行样式
			tableRowClassName({ row }) {
				return row.errorMark === 'Y' ? 'warning-row' : '';
			},
			
			// 合计行
			getSummaries(param) {
				const { columns, data } = param;
				const sums = [];
				columns.forEach((column, index) => {
					if (index === 0) {
						sums[index] = '合计';
						return;
					}
					const values = data.map(item => Number(item[column.property]));
					if (['chargedWeight', 'totalAmount', 'cost', 'profit'].includes(column.property)) {
						sums[index] = values.reduce((prev, curr) => {
							const value = Number(curr);
							if (!isNaN(value)) {
								return prev + curr;
							} else {
								return prev;
							}
						}, 0).toFixed(2);
					} else {
						sums[index] = '';
					}
				});
				return sums;
			},

			// 选择变化
			handleSelectionChange(val) {
				this.selectedRows = val;
			},
			
			// 复制运单号
			copyWaybill(no) {
				navigator.clipboard.writeText(no).then(() => {
					this.$message.success('运单号已复制');
				});
			},

			// 编辑相关
			openEditDialog(row, index) {
				this.editForm = { ...row };
				this.editIndex = index;
				this.editDialogVisible = true;
			},
			
			saveEdit() {
				// 重新计算利润
				this.editForm.profit = (Number(this.editForm.totalAmount) - Number(this.editForm.cost)).toFixed(2);
				// 如果之前有错误标记，且关键字段已填，则清除错误标记
				if (this.editForm.errorMark === 'Y' && this.editForm.destination && this.editForm.chargedWeight) {
					this.editForm.errorMark = 'N';
				}
				
				this.billList.splice(this.editIndex, 1, this.editForm);
				this.editDialogVisible = false;
				this.$message.success('修改已生效');
			},
			
			deleteBillItem(index) {
				this.billList.splice(index, 1);
			},

			// 保存账单
			confirmSaveBill() {
				if (!this.saveForm.billName) {
					this.$message.warning('请输入账单名称');
					return;
				}
				if (!this.saveForm.customerId) {
					this.$message.warning('请选择结算客户');
					return;
				}
				if (this.selectedRows.length === 0) {
					this.$message.warning('请至少在表格中勾选一条数据');
					return;
				}

				this.isSaving = true;
				const payload = {
					...this.saveForm,
					billList: this.selectedRows
				};

				this.apiService.post({
					modules: "saveBill",
					content: payload
				}).then(res => {
					if (res.status) {
						this.$message.success('保存成功');
						this.saveBillWindow = false;
						// 可选：跳转或清空
						// this.$router.push('/finance/bill/customer');
					} else {
						this.$message.error(res.msg || '保存失败');
					}
				}).finally(() => {
					this.isSaving = false;
				});
			},
			
			// 初始化数据
			initData() {
				this.apiService.post({ modules: "newPriceList", content: {} }).then(res => {
					if (res.status) this.priceList = res.data.priceList;
				});
				// 如果有获取客户列表的接口，在这里调用
				// this.apiService.post({ modules: "getCustomerList", content: {} }).then(...)
			}
		},
		mounted() {
			this.initData();
		}
	}
</script>

<style scoped lang="scss">
	.bill-calculation-page {
		padding: 20px;
		background-color: #f5f7fa;
		min-height: 100vh;
		display: flex;
		flex-direction: column;
		gap: 20px;
	}

	.action-card {
		.header-content {
			display: flex;
			justify-content: space-between;
			align-items: center;
			
			.page-title {
				margin: 0;
				font-size: 18px;
				font-weight: 600;
				color: #303133;
			}
			
			.action-buttons {
				display: flex;
				gap: 10px;
				align-items: center;
			}
		}
	}

	.table-card {
		flex: 1;
		display: flex;
		flex-direction: column;
		
		:deep(.el-table__header-wrapper) th {
			background-color: #f5f7fa;
			color: #606266;
			font-weight: bold;
		}
		
		.waybill-link {
			color: #409eff;
			cursor: pointer;
			&:hover {
				text-decoration: underline;
			}
		}
		
		.text-profit { color: #67c23a; }
		.text-loss { color: #f56c6c; }
	}

	.footer-bar {
		position: fixed;
		bottom: 0;
		left: 0;
		right: 0;
		background: #fff;
		padding: 15px 40px;
		box-shadow: 0 -2px 10px rgba(0,0,0,0.05);
		display: flex;
		justify-content: space-between;
		align-items: center;
		z-index: 100;
		
		.selection-info {
			font-size: 14px;
			color: #606266;
			.highlight {
				color: #409eff;
				font-weight: bold;
				font-size: 16px;
				margin: 0 5px;
			}
		}
	}

	.config-form {
		.dialog-tip {
			display: flex;
			align-items: center;
			color: #909399;
			font-size: 12px;
			margin-top: 10px;
			.el-icon { margin-right: 5px; }
		}
	}

	.error-list-container {
		padding: 10px;
		.error-item {
			margin-bottom: 10px;
		}
	}
	
	// 覆盖 Element Plus 默认的错误行样式
	:deep(.el-table .warning-row) {
		background-color: #fef0f0 !important;
		&:hover > td {
			background-color: #fde2e2 !important;
		}
	}
</style>